import {TimeDelta} from "../time.js";
import jStat from "jstat";
import {isEmpty} from "../utils.js";
import DailyStatistics from "./DailyStatistics.js";
import {SentinelAggregate, Statistic} from "./Statistic.js";

const fmt = TimeDelta.formatShortDate;

export class WindowedTestResults {
    constructor({current, previous, previousToCurrent}) {
        this.current = current; // {up: TestResult, down: TestResult}
        this.previous = previous;
        this.previousToCurrent = previousToCurrent;
    }

    drawConclusion(tests) {
        if (tests.up.confidence > 95 && tests.down.confidence < 5) {
            return new TestConclusion({
                firstHalf: tests.firstHalf,
                secondHalf: tests.secondHalf,
                testResult: tests.up,
                direction: Trend.UP,
            });
        } else if (tests.down.confidence > 95 && tests.up.confidence < 5) {
            return new TestConclusion({
                firstHalf: tests.firstHalf,
                secondHalf: tests.secondHalf,
                testResult: tests.down,
                direction: Trend.DOWN,
            });
        } else if (tests.down.baselineN < 3 || tests.up.baselineN < 3 || tests.down.comparisonN < 3 || tests.up.comparisonN < 3) {
            return new TestConclusion({
                testResult: new TestResult({
                    ...tests.up,
                    tStatistic: 0,
                    degreesOfFreedom: 0,
                    pValue: 1,
                    confidence: 0,
                }),
                firstHalf: tests.firstHalf,
                secondHalf: tests.secondHalf,
                direction: Trend.NOT_ENOUGH_DATA
            })
        } else {
            return new TestConclusion({
                testResult: new TestResult({
                    ...tests.up,
                    // tStatistic: 0,
                    // degreesOfFreedom: 0,
                    // pValue: 1,
                    // confidence: 0,
                }),
                firstHalf: tests.firstHalf,
                secondHalf: tests.secondHalf,
                direction: Trend.FLAT
            })
        }
    }

    drawConclusions() {
        return new WindowedTestConclusions({
            windows: {
                current: this.current.window,
                previous: this.previous.window,
                previousToCurrent: this.previousToCurrent.window
            },
            current: this.drawConclusion(this.current),
            previous: this.drawConclusion(this.previous),
            previousToCurrent: this.drawConclusion(this.previousToCurrent),
        });
    }
}

export class WindowedTestConclusions {
    constructor({windows, current, previous, previousToCurrent}) {
        this.windows = windows;
        this.current = current; // {up: TestConclusion, down: TestConclusion}
        this.previous = previous;
        this.previousToCurrent = previousToCurrent;
    }
}

export class TestConclusion {
    constructor({testResult, direction, firstHalf, secondHalf}) {
        this.testResult = testResult;
        this.direction = direction
        this.firstHalf = firstHalf;
        this.secondHalf = secondHalf;
    }

    className() {
        switch (this.direction) {
            case Trend.UP:
                return "trending-up";
            case Trend.DOWN:
                return "trending-down";
            case Trend.FLAT:
                return "trending-flat";
            case Trend.INCONCLUSIVE:
                return "trending-inconclusive";
            case Trend.NOT_ENOUGH_DATA:
                return "trending-not-enough-data";
            default:
                throw new Error("Unrecognized direction: " + this.direction);
        }
    }

    arrow() {
        switch (this.direction) {
            case Trend.UP:
                return "↑";
            case Trend.DOWN:
                return "↓";
            case Trend.FLAT:
                return "↔";
            case Trend.INCONCLUSIVE:
                return "...";
            case Trend.NOT_ENOUGH_DATA:
                return "...";
            default:
                throw new Error("Unrecognized trend: " + trend);
        }
    }
}


export class TestResult {
    constructor({comparisonN, baselineN, tStatistic, degreesOfFreedom, pValue, confidence, comparisonSigma, baselineSigma, comparisonMean, baselineMean}) {
        this.comparisonN = comparisonN;
        this.baselineN = baselineN;
        this.tStatistic = tStatistic;
        this.degreesOfFreedom = degreesOfFreedom;
        this.pValue = pValue;
        this.confidence = confidence;
        this.comparisonSigma = comparisonSigma;
        this.baselineSigma = baselineSigma;
        this.comparisonMean = comparisonMean;
        this.baselineMean = baselineMean;
        this.sigma = (comparisonSigma + baselineSigma) / 2; // Average of the two sigmas
    }
}


export class Trend {

    static UP = "up";
    static DOWN = "down";
    static FLAT = "flat";
    static INCONCLUSIVE = "inconclusive";
    static NOT_ENOUGH_DATA = "not enough data";

    constructor({sample, universe}) {
        this.sample = sample;
        this.universe = universe;
    }


    icon(scope) {
        if (scope === Statistic.UNIVERSE) {
            return "🌍";
        }
        ;
        return "";
    }

}

export class TrendyStatistic {

    constructor({title, operation = Statistic.AVERAGE, dataType = Statistic.NUMBER, currentWindow=null, previousWindow=null}) {
        if (isEmpty(previousWindow?.start || previousWindow?.end)) {
            console.error("previousWindow: ", previousWindow);
            throw new Error("You must provide a previousWindow for TrendyStatistic with start/end dates!");
        }
        if (isEmpty(currentWindow?.start || currentWindow?.end)) {
            console.error("currentWindow: ", currentWindow);
            throw new Error("You must provide a currentWindow for TrendyStatistic with start/end dates!");
        }
        this.title = title;
        this.dailyStatistics = new DailyStatistics(operation, dataType);
        this.operation = operation;
        this.dataType = dataType;
        this.currentWindow = currentWindow;
        this.previousWindow = previousWindow;
        this.completePopulation = [];
    }

    chartKey(scope) {
        return `trendy-statistic-chart-${this.title}-${this.operation}-${scope}`
    }

    add({value, date, isSample = true}) {
        this.completePopulation.push(value);
        return this.dailyStatistics.add({value, date, isSample});
    }

    _getWindows(windowSize) {
        /*
        This method returns two arrays of windowSize elements each representing two consecutive windows of data.
        The second window is the most recent, the first is older. The KEYS are returned, not key, value pairs. They are
        sorted, and the most recent keys are at the end of the array. The keys themselves are not necessarily
        consecutive, they are whatever keys exist in the collected stats, and match the desired window size. No keys
        that do not have corresponding values are added.
         */
        const days = Object.keys(this.dailyStatistics.dailyStats);
        if (days.length < windowSize) {
            return [[], []];
        }
        const secondWindow = days.slice(days.length - windowSize, days.length);
        const firstWindow = days.slice(days.length - (2 * windowSize), days.length - windowSize);
        return [firstWindow, secondWindow];
    }

    _getSamplesOverRangeForPercentile(range) {
        return TimeDelta.daysFrom(range.start, range.end).reduce((samples, day) => {
            day = TimeDelta.floorToDay(day);
            const dailyStat = this.dailyStatistics.dailyStats[day];
            if (dailyStat !== undefined) {
                return [
                    ...samples,
                    ...dailyStat.value()
                ]
            }
            return samples
        }, []);
    }

    _getSamplesOverRange(range) {
        return TimeDelta.daysFrom(range.start, range.end).reduce((samples, day) => {
            day = TimeDelta.floorToDay(day);
            const dailyStat = this.dailyStatistics.dailyStats[day];
            if (dailyStat !== undefined) {
                return [
                    ...samples,
                    ...dailyStat._sample.valueForTTest(this.completePopulation).map(s => { return {sample: s}}),
                    ...dailyStat._universe.valueForTTest(this.completePopulation).map(s => {return {universe: s}})
                ]
            }
            return samples
        }, []);
    }

    _oneTailedWelchTest({baseline, comparison, direction = Trend.UP}) {
        if (comparison.length < 2 || baseline.length < 2) {
            console.warn("Not enough data to perform t-test for ", this.title, " comparison: ", comparison, " baseline: ", baseline);
            const v1 = comparison.length > 0 ? jStat.variance(comparison, true) : null;
            const v2 = baseline.length > 0 ? jStat.variance(baseline, true) : null;
            const m1 = comparison.length > 0 ? jStat.mean(comparison) : 0;
            const m2 = baseline.length > 0 ? jStat.mean(baseline) : 0;
            return new TestResult({
                comparisonN: comparison.length,
                baselineN: baseline.length,
                baselineMean: m2,
                comparisonMean: m1,
                comparisonSigma: v1,
                baselineSigma: v2,
                tStatistic: 0,
                degreesOfFreedom: 0,
                pValue: 1,
                confidence: 0
            });
        }
        const n1 = comparison.length, n2 = baseline.length;
        const m1 = jStat.mean(comparison), m2 = jStat.mean(baseline);
        // const v1 = jStat.variance(comparison, true), v2 = jStat.variance(baseline, true);
        const v1 = jStat.stdev(comparison, true), v2 = jStat.stdev(baseline, true);

        // console.log("Computed variance as", std1, std2, "for comparison: ", comparison, "and baseline: ", baseline);

        if (m1 === undefined || m2 === undefined) {
            console.warn(this.title, ": Sample mean was undefined for comparison: ", comparison, "or baseline: ", baseline);
        }

        const se = Math.sqrt((v1 / n1) + (v2 / n2));
        const t = (m1 - m2) / se;

        const dfNumerator = Math.pow((v1 / n1 + v2 / n2), 2);
        const dfDenominator = (Math.pow(v1 / n1, 2) / (n1 - 1)) + (Math.pow(v2 / n2, 2) / (n2 - 1));
        const df = dfNumerator / dfDenominator;

        const p = direction === Trend.UP
            ? 1 - jStat.studentt.cdf(t, df)
            : jStat.studentt.cdf(t, df);

        return new TestResult({
            comparisonN: comparison.length,
            comparisonSigma: v1,
            baselineSigma: v2,
            comparisonMean: m1,
            baselineMean: m2,
            baselineN: baseline.length,
            tStatistic: t,
            degreesOfFreedom: df,
            pValue: p.toFixed(2),
            confidence: (1 - p) * 100
        });
    }

    _splitWindow(window) {
        // If the window has an odd number of days; then we'll include the middle day in both halves, otherwise they are
        // disjoint.

        const halfwayDate = TimeDelta.getHalfwayBetween({
            startDate: window.start,
            endDate: window.end
        });

        let [leftBound, rightBound] = [
            TimeDelta.floorToDay(halfwayDate),
            TimeDelta.floorToDay(new Date(halfwayDate.getTime() + 24 * 60 * 60 * 1000))
        ];

        return [[window.start, leftBound], [rightBound, window.end]];

    }

    _sliceSamples(window) {
        /*
        This method slices the samples into two halves, returning the first half and second half.
         */
        const [[leftLeftBound, leftRightBound], [rightLeftBound, rightRightBound]] = this._splitWindow(window);

        const whole = this._getSamplesOverRange({
            start: leftLeftBound,
            end: rightRightBound
        })
        const firstHalf = this._getSamplesOverRange({
            start: leftLeftBound,
            end: leftRightBound,
        });
        const secondHalf = this._getSamplesOverRange({
            start: rightLeftBound,
            end: rightRightBound
        });
        return [whole, firstHalf, secondHalf];
    }

    _sliceSamplesForPercentile(window) {
        /*
        This method slices the samples into two halves, returning the first half and second half.
         */
        const [[leftLeftBound, leftRightBound], [rightLeftBound, rightRightBound]] = this._splitWindow(window);
        const whole = this._getSamplesOverRangeForPercentile({
            start: leftLeftBound,
            end: rightRightBound
        })
        const firstHalf = this._getSamplesOverRangeForPercentile({
            start: leftLeftBound,
            end: leftRightBound,
        });
        const secondHalf = this._getSamplesOverRangeForPercentile({
            start: rightLeftBound,
            end: rightRightBound
        });

        return [whole, firstHalf, secondHalf];
    }

    _applyTests({baseline, comparison}) {
        const up = this._oneTailedWelchTest({
            baseline: baseline,
            comparison: comparison,
            direction: Trend.UP
        })
        const down = this._oneTailedWelchTest({
            baseline: baseline,
            comparison: comparison,
            direction: Trend.DOWN
        });
        return [up, down]
    }

    _sliceWindows(window) {
        if (this.dataType === Statistic.PERCENTILE) {
            return this._sliceSamplesForPercentile(window);
        }
        return this._sliceSamples(window);
    }

    _testForTrends({comparisonWindow, baselineWindow}) {
        const [baselineSamples, baselineFirstHalfSamples, baselineSecondHalfSamples] = this._sliceWindows(baselineWindow);
        const [comparisonSamples, comparisonFirstHalfSamples, comparisonSecondHalfSamples] = this._sliceWindows(comparisonWindow);


        const [trendingUpSample, trendingDownSample] = this._applyTests({
            baseline: baselineSamples.map(s => s.sample).filter(s => s !== undefined),
            comparison: comparisonSamples.map(s => s.sample).filter(s => s !== undefined)
        })
        const [trendingUpUniverse, trendingDownUniverse] = this._applyTests({
            baseline: baselineSamples.map(s => s.universe).filter(s => s !== undefined),
            comparison: comparisonSamples.map(s => s.universe).filter(s => s !== undefined)
        })
        const [baselineTrendingUpSample, baselineTrendingDownSample] = this._applyTests({
            comparison: baselineSecondHalfSamples.map(s=> s.sample).filter(s => s !== undefined),
            baseline: baselineFirstHalfSamples.map(s => s.sample).filter(s => s !== undefined)
        })
        const [baselineTrendingUpUniverse, baselineTrendingDownUniverse] = this._applyTests({
            comparison: baselineSecondHalfSamples.map(s=> s.universe).filter(s => s !== undefined),
            baseline: baselineFirstHalfSamples.map(s => s.universe).filter(s => s !== undefined)
        });
        const [comparisonTrendingUpSample, comparisonTrendingDownSample] = this._applyTests({
            comparison: comparisonSecondHalfSamples.map(s=> s.sample).filter(s => s !== undefined),
            baseline: comparisonFirstHalfSamples.map(s => s.sample).filter(s => s !== undefined)
        });
        const [comparisonTrendingUpUniverse, comparisonTrendingDownUniverse] = this._applyTests({
            comparison: comparisonSecondHalfSamples.map(s=> s.universe).filter(s => s !== undefined),
            baseline: comparisonFirstHalfSamples.map(s => s.universe).filter(s => s !== undefined)
        });

        return {
            sample: new WindowedTestResults({
                current: {
                    up: comparisonTrendingUpSample,
                    down: comparisonTrendingDownSample,
                    firstHalf: this.aggregateOver({
                        startDate: comparisonWindow.start,
                        endDate: TimeDelta.getHalfwayBetween({
                            startDate: comparisonWindow.start,
                            endDate: comparisonWindow.end
                        })
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: TimeDelta.getHalfwayBetween({
                            startDate: comparisonWindow.start,
                            endDate: comparisonWindow.end
                        }),
                        endDate: comparisonWindow.end
                    }).displayValue()
                },
                previous: {
                    up: baselineTrendingUpSample,
                    down: baselineTrendingDownSample,
                    firstHalf: this.aggregateOver({
                        startDate: baselineWindow.start,
                        endDate: TimeDelta.getHalfwayBetween({
                            startDate: baselineWindow.start,
                            endDate: baselineWindow.end
                        })
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: TimeDelta.getHalfwayBetween({
                            startDate: baselineWindow.start,
                            endDate: baselineWindow.end
                        }),
                        endDate: baselineWindow.end
                    }).displayValue()
                },
                previousToCurrent: {
                    up: trendingUpSample,
                    down: trendingDownSample,
                    firstHalf: this.aggregateOver({
                        startDate: baselineWindow.start,
                        endDate: baselineWindow.end
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: comparisonWindow.start,
                        endDate: comparisonWindow.end
                    }).displayValue()
                }
            }),
            universe: new WindowedTestResults({
                current: {
                    window: comparisonWindow,
                    up: comparisonTrendingUpUniverse,
                    down: comparisonTrendingDownUniverse,
                    firstHalf: this.aggregateOver({
                        startDate: comparisonWindow.start,
                        endDate: TimeDelta.getHalfwayBetween({
                            startDate: comparisonWindow.start,
                            endDate: comparisonWindow.end
                        })
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: TimeDelta.getHalfwayBetween({
                            startDate: comparisonWindow.start,
                            endDate: comparisonWindow.end
                        }),
                        endDate: comparisonWindow.end
                    }).displayValue()
                },
                previous: {
                    window: baselineWindow,
                    up: baselineTrendingUpUniverse,
                    down: baselineTrendingDownUniverse,
                    firstHalf: this.aggregateOver({
                        startDate: baselineWindow.start,
                        endDate: TimeDelta.getHalfwayBetween({
                            startDate: baselineWindow.start,
                            endDate: baselineWindow.end
                        })
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: TimeDelta.getHalfwayBetween({
                            startDate: baselineWindow.start,
                            endDate: baselineWindow.end
                        }),
                        endDate: baselineWindow.end
                    }).displayValue()
                },
                previousToCurrent: {
                    window: {start: baselineWindow.start, end: comparisonWindow.end},
                    up: trendingUpUniverse,
                    down: trendingDownUniverse,
                    firstHalf: this.aggregateOver({
                        startDate: baselineWindow.start,
                        endDate: baselineWindow.end
                    }).displayValue(),
                    secondHalf: this.aggregateOver({
                        startDate: comparisonWindow.start,
                        endDate: comparisonWindow.end
                    }).displayValue()
                }
            })
        };
    }

    trend() {
        /*
        This tells you if the current is trending up, down, flat, or inconclusive compared to the previous window.
         */
        const {sample, universe} = this._testForTrends({
            baselineWindow: this.previousWindow,
            comparisonWindow: this.currentWindow
        });

        return new Trend({
            sample: sample.drawConclusions(),
            universe: universe.drawConclusions()
        });
    }

    binToWeek() {
        return Object.entries(this.dailyStatistics.dailyStats).reduce((acc, [day, aggregate]) => {
            const sunday = TimeDelta.getSunday(new Date(day));
            if (acc[sunday] === undefined) {
                acc[sunday] = new aggregate.constructor(aggregate.operation, aggregate.dataType);
            }
            acc[sunday] = acc[sunday].mergeInPlace(aggregate)
            return acc;
        }, {});
    }

    bin(binSizeInDays) {
        // Extract and sort keys
        if (binSizeInDays === undefined || binSizeInDays <= 0 || binSizeInDays === 1) {
            return this.dailyStatistics.dailyStats; // No binning needed
        }
        const dateKeys = Object.keys(this.dailyStatistics.dailyStats).map(d => TimeDelta.floorToDay(new Date(d)));
        if (dateKeys.length === 0) return {};

        dateKeys.sort((a, b) => b - a); // Descending order

        // Floor latest date to midnight
        const latest = TimeDelta.floorToDay(new Date(this.dailyStatistics.lastDay.getTime()));
        const earliest = TimeDelta.floorToDay(new Date(this.dailyStatistics.firstDay.getTime()));

        const binSizeMs = binSizeInDays * 24 * 60 * 60 * 1000;
        const binned = {};

        for (
            let binStart = new Date(latest);
            binStart >= earliest;
            binStart = new Date(binStart.getTime() - binSizeMs)
        ) {
            const binEnd = new Date(binStart.getTime() + binSizeMs);
            const binKey = binStart;

            binned[binKey] = [];

            for (const d of dateKeys) {
                if (d >= binStart && d < binEnd) {
                    binned[binKey].push(this.dailyStatistics.dailyStats[d]);
                }
            }
        }

        // Aggregate the binned data
        for (const [key, stats] of Object.entries(binned)) {
            if (isEmpty(stats)) {
                continue; // Skip empty bins
            }
            binned[key] = stats[0].constructor.aggregate(stats, this.dataType);
        }

        return binned;
    }

    _getDatasetData({title, alignedWindow, startDate, endDate, data, scope, period, type}) {
        if (!period) {
            console.trace();
        }
        const dataset = {
            label: title + ` (${TimeDelta.formatShortDate(startDate)}-${TimeDelta.formatShortDate(endDate)})`,
            data: alignedWindow.map((date, idx) => {
                if (isEmpty(date)) {
                    return this._getPoint({idx, y: 0, chartType: type});
                }
                const dateKey = new Date(date).toLocaleDateString();
                if (isEmpty(data[dateKey])) {
                    return this._getPoint({idx, y: 0, chartType: type});
                }
                if (startDate <= date < endDate) {
                    return this._getPoint({idx, y: data[dateKey][scope], chartType: type});
                }
                return this._getPoint({idx, y: 0, chartType: type});
            }).filter(o => o !== null),
            period: period,
            pointRadius: 1,
            borderWidth: 1,
            borderDash: [2, 2]
        }
        if (type) {
            dataset.type = type;
        }
        return dataset;
    }

    _getPoint({idx, y, chartType}) {
        if (chartType === "bar" || chartType === "pie" || chartType === "doughnut") {
            return y;
        }
        return [idx, y]
    }

    _getDatasetsForWindow({alignedWindow, sampleChartType, values, startDate, endDate, excludeUniverse = false, period = "current"}) {
        const datasets = [
            this._getDatasetData({
                title: this.title,
                alignedWindow,
                startDate,
                endDate,
                data: values,
                scope: Statistic.SAMPLE,
                period,
                type: sampleChartType
            })
        ];

        if (!excludeUniverse) {
            datasets.push(this._getDatasetData({
                title: "Global",
                alignedWindow,
                startDate,
                endDate,
                data: values,
                period,
                scope: Statistic.UNIVERSE,
            }));
        }
        return datasets;
    }

    _getEmptyDatasetContainer(daysOfTheWeek, extraData) {
        return {
            labels: daysOfTheWeek,
            extraData: extraData,
            datasets: []
        }
    }

    chartDatasets({
                      binSizeDays,
                      excludeUniverse = false,
                      sampleChartType = "bar",
                      splitByWindow = false
                  }) {
        /*
        This method first aligns the dataset so that days of the week line up. Then it bins the dataset into calendar
        bins, which remain aligned.

        It then indexes both data sets filling in gaps for missing days/bins.
         */
        const bins = this.bin(binSizeDays || 1);

        const values = Object.entries(bins).reduce((acc, [day, aggregate]) => {
            const dateKey = new Date(day).toLocaleDateString();
            if (isEmpty(dateKey)) {
                console.error("Empty dateKey found in bins: ", bins, "derived from day: ", day, "of type", typeof day, "and aggregate: ", aggregate);
            }
            if (aggregate.operation !== Statistic.PERCENTILE) {
                acc[dateKey] = aggregate.value();
            } else {
                const aggregated = aggregate.value().reduce((acc, val) => {
                    acc.sample.count += 1
                    acc.sample.sum += val.sample || 0;
                    acc.universe.count += 1
                    acc.universe.sum += val.universe || 0;
                    return acc;
                }, {
                    sample: {
                        count: 0,
                        sum: 0
                    },
                    universe: {
                        count: 0,
                        sum: 0
                    }
                });
                acc[dateKey] = {sample: aggregated.sample.sum / aggregated.sample.count || 0, universe: aggregated.universe.sum / aggregated.universe.count || 0};
            }
            return acc;
        }, {});

        const [alignedPrevious, alignedCurrent] = TimeDelta.alignWindows(
            this.previousWindow,
            this.currentWindow
        );

        const week = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const daysOfTheWeek = alignedPrevious.map((date, idx) => {
            const dateObj = date === null ? alignedCurrent[idx] : date;
            return week[dateObj.getDay()];
        })

        const extraData ={
            title: this.title,
            windows: {
                previous: this.previousWindow,
                current: this.currentWindow
            },
            alignedPrevious,
            alignedCurrent,
        };

        if (splitByWindow) {
            return {
                previous: {
                    ...this._getEmptyDatasetContainer(daysOfTheWeek, extraData),
                    datasets: this._getDatasetsForWindow({
                        alignedWindow: alignedPrevious,
                        startDate: this.previousWindow.start,
                        endDate: this.previousWindow.end,
                        sampleChartType,
                        values,
                        excludeUniverse,
                        period: "previous"
                    })
                },
                current: {
                    ...this._getEmptyDatasetContainer(daysOfTheWeek, extraData),
                    datasets: this._getDatasetsForWindow({
                        alignedWindow: alignedCurrent,
                        startDate: this.currentWindow.start,
                        endDate: this.currentWindow.end,
                        sampleChartType,
                        values,
                        excludeUniverse,
                        period: "current"
                    })
                }
            }
        }

        return {
            ...this._getEmptyDatasetContainer(daysOfTheWeek, extraData),
            datasets: [
                ...this._getDatasetsForWindow({
                    alignedWindow: alignedPrevious,
                    sampleChartType,
                    startDate: this.previousWindow.start,
                    endDate: this.previousWindow.end,
                    values,
                    excludeUniverse,
                    period: "previous"
                }),
                ...this._getDatasetsForWindow({
                    alignedWindow: alignedCurrent,
                    startDate: this.currentWindow.start,
                    endDate: this.currentWindow.end,
                    sampleChartType,
                    values,
                    excludeUniverse,
                    period: "current"
                }),
            ]
        };
    }

    aggregateOver({startDate, endDate}) {
        if (isEmpty(this.dailyStatistics)) {
            throw new Error(`this.onlineDailyStatistic  was never set for ${this.title}!`);
        }

        const stats = Object.entries(this.dailyStatistics.dailyStats).reduce(
            (acc, [day, statistic]) => {
                day = new Date(day);
                if (day <= endDate && day >= startDate) {
                    acc.push(statistic);
                }
                return acc;
            }, []);

        // These should return one of {sample, universe} for each of {previous, current}. I think that already happens maybe in constructor.aggregate.
        if (isEmpty(stats)) {
            return new SentinelAggregate()
        }

        return stats[0].constructor.aggregate(stats, this.dataType)
    }

    aggregate() {
        if (isEmpty(this.dailyStatistics)) {
            throw new Error(`this.onlineDailyStatistic  was never set for ${this.title}!`);
        }

        const previous = this.aggregateOver({startDate: this.previousWindow.start, endDate: this.previousWindow.end});
        const current = this.aggregateOver({startDate: this.currentWindow.start, endDate: this.currentWindow.end});
        const previousToCurrent = this.aggregateOver({startDate: this.previousWindow.start, endDate: this.currentWindow.end});

        // These should return one of {sample, universe} for each of {previous, current}. I think that already happens maybe in constructor.aggregate.
        return {previous, current, previousToCurrent};
    }

    value() {
        /*
        Returns {sample: value, universe: value} where value is the appropriate statistic. For percentile it returns null for universe.
         */
        // These should return one of {sample, universe} for each of {previous, current}. I think that already happens maybe in constructor.aggregate.
        const {previous, current, previousToCurrent} = this.aggregate();

        return {
            previous: previous.value(),
            current: current.value(),
            previousToCurrent: previousToCurrent.value()
        };
    }


    displayValue() {
        // These should return one of {sample, universe} for each of {previous, current}. I think that already happens maybe in constructor.aggregate.
        const {previous, current, previousToCurrent} = this.aggregate();
        return {
            previous: previous.displayValue(),
            current: current.displayValue(),
            previousToCurrent: previousToCurrent.displayValue()
        }
    }
}


export default TrendyStatistic;
